import { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
    FormItem,
    InputTextarea,
    MarketingBannerAdvantages,
    Notification,
    NotificationLevel,
    WizardBarItemStatus,
    useWizardStepsContext,
} from '@steelbuy/ui-primitive';
import { RoutePath } from '../../../router/Routes';
import { FormLayout } from '../../../views/layouts/form-layout/FormLayout';
import { WizardActionBar } from '../../wizard-action-bar/WizardActionBar';
import { RFQStep } from '../CreateRFQ.enums';
import { getStepRoute, hasFormData, isStep2Valid } from '../createRFQUtil';
import { useRfqFormContext } from '../RfqFormContext';
import { isStep3RequestQuote, Step3RequestQuoteType } from '../Schema';

import './Step3RequestQuote.scss';

const MAX_LENGTH = 30000;
interface SaveDraftContext {
    onSaveDraft: () => void;
    loadingState: boolean;
}
export const Step3RequestQuote = () => {
    const { t } = useTranslation(['translation', 'uiDomain']);
    const { loadingState } = useOutletContext<SaveDraftContext>();

    const { formData, updateFormData, setUpdateValuesCallback, draftId } = useRfqFormContext();
    const {
        control,
        handleSubmit,
        formState: { isValid, errors },
        getValues,
        trigger,
    } = useForm<Step3RequestQuoteType>({
        defaultValues: { comments: isStep3RequestQuote(formData) ? formData.comments : '' },
        mode: 'all',
    });
    const { updateStepStatus, isStepVisited, visitedSteps } = useWizardStepsContext();

    const navigate = useNavigate();

    const clickedButton = useRef<'next' | 'back' | null>(null);

    useEffect(() => {
        if (draftId || isStepVisited(RFQStep.SHIPPING_DETAILS)) {
            trigger();
        }
    }, [draftId, visitedSteps]);

    const updateStatus = () => updateStepStatus(RFQStep.COMMENTS, WizardBarItemStatus.SUCCESS);

    useEffect(() => {
        setUpdateValuesCallback((setStepStatus) => {
            updateFormData(getValues());
            if (setStepStatus) {
                updateStatus();
            }
        });
    }, [isValid]);

    if (!hasFormData(formData)) {
        return null;
    }

    const onHandleSubmit = (data: Step3RequestQuoteType) => {
        updateFormData(data);
        updateStatus();
        if (clickedButton.current === 'back') {
            navigate(getStepRoute(2, true));
        }
        if (clickedButton.current === 'next') {
            navigate(RoutePath.CREATE_REQUEST_QUOTE_SUMMARY, { replace: true });
        }
    };

    const onSubmit = handleSubmit(
        (data) => {
            onHandleSubmit(data);
        },
        () => {
            onHandleSubmit(getValues());
        }
    );

    const valid = isStep2Valid(formData) && isValid;
    let errorMessage = '';
    if (!valid) {
        errorMessage = t('translation:application.pickupAddressStep.incompleteInformationNotification');
    }

    return (
        <div className="create-rfq-step3-request-quote">
            <form onSubmit={onSubmit}>
                <FormLayout>
                    <FormItem
                        header={t('application.createRFQ.additionalComments.header')}
                        description={t('application.createRFQ.additionalComments.description')}
                    >
                        <Controller
                            name="comments"
                            control={control}
                            rules={{
                                maxLength: {
                                    message: t('uiDomain:materialAdditionalComment.maxCharacterError'),
                                    value: MAX_LENGTH,
                                },
                            }}
                            render={({ field: { value, onChange } }) => (
                                <InputTextarea
                                    label={t('uiDomain:materialAdditionalComment.label')}
                                    placeholder={t('uiDomain:materialAdditionalComment.placeholder')}
                                    onChange={onChange}
                                    value={value}
                                    error={errors.comments?.message}
                                />
                            )}
                        />
                    </FormItem>

                    <MarketingBannerAdvantages
                        header={t('translation:application.pickupAddressStep.anonymityBannerHeader')}
                        text={t('translation:application.pickupAddressStep.anonymityBannerText')}
                    />

                    {errorMessage && (
                        <Notification level={NotificationLevel.ERROR} message={errorMessage} disableScroll />
                    )}
                    <WizardActionBar
                        onNext={() => {
                            clickedButton.current = 'next';
                            onSubmit();
                        }}
                        isSubmitDisabled={!valid}
                        nextLabel={t('application.createRFQ.viewSummary')}
                        onBack={() => {
                            clickedButton.current = 'back';
                            onSubmit();
                        }}
                        isLoading={loadingState}
                    />
                </FormLayout>
            </form>
        </div>
    );
};
